/**
 * Movement rules (house-rule model, 2026-07-02). See docs/GAME-RULES.md.
 *  - AT MOST ONE ship per tile, everywhere (friend or foe). No stacking, no
 *    harbor exception.
 *  - No zone of control: a ship moves freely to any empty water tile within its
 *    movement allowance. Adjacency only matters at combat time (see combat.ts).
 *  - Enemy-occupied tiles are impassable (you can't enter or stop on them).
 *    Friendly-occupied tiles may be passed through but not stopped on.
 * Ported from legacy/admirals-return.html (reachable/pathTo/executeMove/canStop),
 * then diverged deliberately from the original manual's stacking/contact rules.
 */
import { key, neighbors } from './board';
import { UNITS } from './ships';
import { type GameState, type Ship, type Side } from './state';
import { visibleTo } from './fog';

/** How a mover decides an enemy tile is a wall. Defaults to visibleTo (the strict,
 *  fog-honest rule the AI reasons under). The human player passes visibleOnBoard so
 *  pathing blocks on every enemy they can actually see on the board — otherwise a
 *  ship routes straight through an adjacent enemy that the strict rule hasn't
 *  "spotted" yet. See rules/fog.ts for the two predicates. */
export type EnemyVisible = (g: GameState, side: Side, e: Ship) => boolean;

export function moveAllowance(s: Ship): number {
  if (s.type === 'SS') return s.submerged ? 2 : 3;
  return UNITS[s.type].move;
}

/** Can `s` legally END its move on (c,r)? Only if no other ship is already there. */
export function canStop(g: GameState, s: Ship, c: number, r: number): boolean {
  return !g.ships.some((x) => x.id !== s.id && x.c === c && x.r === r);
}

export interface ReachInfo {
  c: number;
  r: number;
  /** reachable to pass through (a friendly ship sits here), but not a legal stop */
  stop: boolean;
}

/**
 * All tiles `s` can move to/through this turn. Enemy-occupied tiles are walls;
 * friendly-occupied tiles can be traversed but not stopped on. seeAll=true is
 * used by tests/AI debug to ignore fog when judging enemy tiles.
 */
export function reachable(g: GameState, s: Ship, seeAll = false, enemyVisible: EnemyVisible = visibleTo): Record<string, ReachInfo> {
  const maxMp = moveAllowance(s) - s.moved;
  const out: Record<string, ReachInfo> = {};
  if (maxMp <= 0 || s.anchored || s.done) return out;
  const q: [number, number, number][] = [[s.c, s.r, 0]];
  const dist: Record<string, number> = { [key(s.c, s.r)]: 0 };
  while (q.length) {
    const [c, r, d] = q.shift()!;
    if (d >= maxMp) continue;
    for (const [nc, nr] of neighbors(g, c, r)) {
      const k = key(nc, nr);
      if (g.tiles[nr]![nc] === 'land') continue;
      if (k in dist) continue;
      const here = g.ships.filter((x) => x.c === nc && x.r === nr);
      const enemy = here.find((x) => x.side !== s.side && (seeAll || enemyVisible(g, s.side, x)));
      if (enemy) continue; // enemy-occupied → impassable wall
      const occupied = here.length > 0; // a friendly ship sits here → pass-through only
      dist[k] = d + 1;
      out[k] = { c: nc, r: nr, stop: occupied };
      q.push([nc, nr, d + 1]);
    }
  }
  return out;
}

/** BFS path toward (tc,tr), trimmed to this turn's movement points. */
export function pathTo(
  g: GameState, s: Ship, tc: number, tr: number, seeAll = false, enemyVisible: EnemyVisible = visibleTo,
): [number, number][] | null {
  const maxMp = moveAllowance(s) - s.moved;
  if (maxMp <= 0) return null;
  const start = key(s.c, s.r);
  const q: [number, number][] = [[s.c, s.r]];
  const par: Record<string, string | null> = { [start]: null };
  while (q.length) {
    const [c, r] = q.shift()!;
    if (c === tc && r === tr) break;
    for (const [nc, nr] of neighbors(g, c, r)) {
      const k = key(nc, nr);
      if (g.tiles[nr]![nc] === 'land' || k in par) continue;
      const here = g.ships.filter((x) => x.c === nc && x.r === nr);
      const enemy = here.find((x) => x.side !== s.side && (seeAll || enemyVisible(g, s.side, x)));
      if (enemy) continue; // enemy-occupied → impassable (never enqueue)
      par[k] = key(c, r);
      q.push([nc, nr]);
    }
  }
  const tk = key(tc, tr);
  if (!(tk in par)) return null;
  const path: [number, number][] = [];
  let cur: string | null = tk;
  while (cur && cur !== start) {
    const [c, r] = cur.split(',').map(Number) as [number, number];
    path.unshift([c, r]);
    cur = par[cur] ?? null;
  }
  return path.slice(0, maxMp);
}

/** Walk `s` along `path`, spending movement. The ship passes THROUGH friendly
 *  ships (a dense one-per-tile fleet must not box its own hulls in — pathTo/
 *  reachable already route through friendlies) but comes to rest ONLY on an empty
 *  tile, so one-ship-per-tile always holds. It stops before an enemy tile (never
 *  on a legal path, but guarded defensively). Movement points spent = distance to
 *  the resting tile.
 *
 *  Returns the tiles ACTUALLY traversed (origin excluded, resting tile last), which
 *  can be SHORTER than `path` when the ship halts before an enemy. Callers animate
 *  and draw the move trail from this, not the requested path, so the sprite/trail
 *  never glide through a tile the ship didn't really reach. */
export function executeMove(g: GameState, s: Ship, path: readonly (readonly [number, number])[]): [number, number][] {
  const maxMp = moveAllowance(s) - s.moved;
  let restIdx = -1; // furthest path index the ship can legally rest on (-1 = don't move)
  for (let i = 0; i < path.length && i < maxMp; i++) {
    const [c, r] = path[i]!;
    if (g.ships.some((x) => x.side !== s.side && x.c === c && x.r === r)) break; // enemy — stop before it
    if (!g.ships.some((x) => x.id !== s.id && x.c === c && x.r === r)) restIdx = i; // empty → can rest here
  }
  if (restIdx < 0) return [];
  s.c = path[restIdx]![0];
  s.r = path[restIdx]![1];
  s.moved += restIdx + 1;
  return path.slice(0, restIdx + 1).map(([c, r]) => [c, r]);
}

/** Subs may toggle submerged/surfaced only before moving this turn. */
export function toggleSubmerged(s: Ship): boolean {
  if (s.type !== 'SS' || s.moved > 0 || s.done) return false;
  s.submerged = !s.submerged;
  return true;
}

/** Toggle anchored/released (manual: D key). Anchoring holds position and ends
 *  the ship's turn; the flag persists across turns until released. Un-anchoring
 *  clears the standing order and hands the turn back ONLY if the ship hasn't
 *  actually moved or fought yet (so releasing never refunds spent movement).
 *  Returns false if the action can't apply (already done for another reason). */
export function toggleAnchored(s: Ship): boolean {
  if (s.anchored) {
    s.anchored = false;
    if (s.moved === 0 && !s.fought) s.done = false;
    return true;
  }
  if (s.done) return false;
  s.anchored = true;
  s.done = true;
  return true;
}
